import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superadminApi, type SystemTemplateDto } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ImageIcon, Pencil, Plus, Trash2, X } from 'lucide-react'

// ── Upload modal ─────────────────────────────────────────────────────────────

function UploadModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [templateType, setTemplateType] = useState<'Png' | 'Svg'>('Png')
  const [isPortrait, setIsPortrait] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected')
      const fd = new FormData()
      fd.append('name', name)
      fd.append('description', description)
      fd.append('templateType', templateType)
      fd.append('isPortrait', String(isPortrait))
      fd.append('file', file)
      return superadminApi.uploadSystemTemplate(fd)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] })
      onClose()
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail ?? 'Upload failed.')
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer">
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold text-gray-900 mb-5">Upload system template</h2>

        <form
          onSubmit={e => { e.preventDefault(); setError(null); mutation.mutate() }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="tmpl-name">Name</Label>
            <Input id="tmpl-name" value={name} onChange={e => setName(e.target.value)} required placeholder="Classic Border" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tmpl-desc">Description <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input id="tmpl-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Ornate border with corner flourishes" />
          </div>

          <div className="space-y-1.5">
            <Label>Type</Label>
            <div className="flex gap-3">
              {(['Png', 'Svg'] as const).map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="templateType"
                    value={t}
                    checked={templateType === t}
                    onChange={() => setTemplateType(t)}
                    className="accent-indigo-600"
                  />
                  <span className="text-sm text-slate-700">{t}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              {templateType === 'Png'
                ? 'PNG: transparent background with black linework — colour applied at render time.'
                : 'SVG: vector format — colour substitution applied at render time.'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Orientation</Label>
            <div className="flex gap-4">
              {(['Landscape', 'Portrait'] as const).map(o => (
                <label key={o} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="upload-orientation"
                    checked={isPortrait === (o === 'Portrait')}
                    onChange={() => setIsPortrait(o === 'Portrait')}
                    className="accent-indigo-600"
                  />
                  <span className="text-sm text-slate-700">{o}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>File</Label>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
            >
              {file ? (
                <p className="text-sm text-slate-700 font-medium">{file.name}</p>
              ) : (
                <>
                  <ImageIcon size={24} className="mx-auto text-slate-300 mb-1" />
                  <p className="text-sm text-slate-500">Click to choose a file</p>
                  <p className="text-xs text-slate-400 mt-0.5">{templateType === 'Png' ? 'PNG with transparency' : 'SVG file'}</p>
                </>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={templateType === 'Png' ? 'image/png' : 'image/svg+xml'}
              className="hidden"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || !file || !name}>
              {mutation.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditModal({ template, onClose }: { template: SystemTemplateDto; onClose: () => void }) {
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState(template.name)
  const [description, setDescription] = useState(template.description ?? '')
  const [templateType, setTemplateType] = useState<'Png' | 'Svg'>(template.templateType)
  const [isPortrait, setIsPortrait] = useState(template.isPortrait)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const detailsMutation = useMutation({
    mutationFn: () =>
      superadminApi.updateSystemTemplate(template.id, {
        name,
        description: description || null,
        isPortrait,
      }),
    onError: (err: any) => {
      setError(err.response?.data?.detail ?? 'Update failed.')
    },
  })

  const fileMutation = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData()
      fd.append('templateType', templateType)
      fd.append('file', f)
      return superadminApi.replaceSystemTemplateFile(template.id, fd)
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail ?? 'File upload failed.')
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    try {
      await detailsMutation.mutateAsync()
      if (file) await fileMutation.mutateAsync(file)
      queryClient.invalidateQueries({ queryKey: ['system-templates'] })
      onClose()
    } catch {
      // errors already set by individual mutation onError handlers
    }
  }

  const isPending = detailsMutation.isPending || fileMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer">
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold text-gray-900 mb-5">Edit template</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={name} onChange={e => setName(e.target.value)} required />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-desc">Description</Label>
            <Input id="edit-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
          </div>

          <div className="space-y-1.5">
            <Label>Orientation</Label>
            <div className="flex gap-4">
              {(['Landscape', 'Portrait'] as const).map(o => (
                <label key={o} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="edit-orientation"
                    checked={isPortrait === (o === 'Portrait')}
                    onChange={() => setIsPortrait(o === 'Portrait')}
                    className="accent-indigo-600"
                  />
                  <span className="text-sm text-slate-700">{o}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Replace image (optional) */}
          <div className="space-y-1.5">
            <Label>Replace image <span className="text-slate-400 font-normal">(optional)</span></Label>

            <div className="space-y-2">
              <div className="flex gap-3">
                {(['Png', 'Svg'] as const).map(t => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="edit-templateType"
                      value={t}
                      checked={templateType === t}
                      onChange={() => setTemplateType(t)}
                      className="accent-indigo-600"
                    />
                    <span className="text-sm text-slate-700">{t}</span>
                  </label>
                ))}
              </div>

              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
              >
                {file ? (
                  <p className="text-sm text-slate-700 font-medium">{file.name}</p>
                ) : (
                  <>
                    <ImageIcon size={20} className="mx-auto text-slate-300 mb-1" />
                    <p className="text-sm text-slate-500">Click to choose a new file</p>
                    <p className="text-xs text-slate-400 mt-0.5">Leave empty to keep the current image</p>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept={templateType === 'Png' ? 'image/png' : 'image/svg+xml'}
                className="hidden"
                onChange={e => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isPending || !name}>
              {isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SystemTemplatesPage() {
  const queryClient = useQueryClient()
  const [showUpload, setShowUpload] = useState(false)
  const [editing, setEditing] = useState<SystemTemplateDto | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<SystemTemplateDto | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['system-templates'],
    queryFn: () => superadminApi.getSystemTemplates().then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => superadminApi.deleteSystemTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-templates'] })
      setDeleteConfirm(null)
      setDeleteError(null)
    },
    onError: (err: any) => {
      setDeleteError(err.response?.data?.detail ?? 'Delete failed.')
    },
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            Certificate backgrounds available to all tenants. PNG templates use transparent + black linework
            and are recoloured at render time.
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Plus size={15} className="mr-1.5" /> Upload template
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <ImageIcon size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No system templates yet. Upload one to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {templates.map(t => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm group">
              {/* Preview */}
              <div
                className="bg-slate-100 flex items-center justify-center relative overflow-hidden"
                style={{ aspectRatio: t.isPortrait ? '1/1.414' : '1.414/1' }}
              >
                {t.thumbnailUrl ? (
                  <img
                    src={t.thumbnailUrl}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={28} className="text-slate-300" />
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                    t.isPortrait ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {t.isPortrait ? 'Portrait' : 'Landscape'}
                  </span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                    t.templateType === 'Svg' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {t.templateType}
                  </span>
                </div>
              </div>

              {/* Info + actions */}
              <div className="p-3">
                <p className="text-sm font-medium text-slate-800 truncate">{t.name}</p>
                {t.description && (
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</p>
                )}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setEditing(t)}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 transition-colors cursor-pointer"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    onClick={() => { setDeleteConfirm(t); setDeleteError(null) }}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors cursor-pointer ml-auto"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-base font-semibold text-slate-900 mb-2">Delete template?</h2>
            <p className="text-sm text-slate-600 mb-4">
              <strong>{deleteConfirm.name}</strong> will be permanently deleted. Any certificate designs using it will need to be updated.
            </p>
            {deleteError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">{deleteError}</p>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteError(null) }}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} />}
      {editing && <EditModal template={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}
